import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, MessageCircle, Award, Clock, Users, Star, Sparkles, Heart, Hand, Baby, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { PATIENTS_STAT } from "@/constants/practiceStats";
import { WHATSAPP_URL as WHATSAPP_LINK, PHONE_DISPLAY, GOOGLE_MAPS_URL, GOOGLE_BUSINESS_RATING } from "@/constants/contact";

// Hero Section
const HeroSection = () => (
  <section className="bg-warm-white pt-24 pb-16 md:pb-24" data-testid="about-hero">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-sm font-medium tracking-widest text-gold uppercase mb-3 md:mb-4">Plastic &amp; Reconstructive Surgeon · Mumbai</p>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-premium-purple leading-[1.12] sm:leading-[1.15] mb-5 sm:mb-6">About Dr. Vicky Jain — Plastic &amp; Reconstructive Surgeon, Mumbai</h1>
          <p className="text-gold text-sm mb-1 leading-snug">
            M.Ch. (Plastic &amp; Cosmetic Surgery) · Board-Certified Surgeon
          </p>
          <p className="text-gold text-sm mb-1 leading-snug">
            Fellowship in Cosmetic Surgery, Switzerland
          </p>
          <p className="text-gold text-sm mb-3 sm:mb-4 leading-snug">
            Fellowship in Lasers, Association of Plastic Surgeons of India (APSI)
          </p>
          <p className="text-base sm:text-lg text-deep-navy/70 mb-4 sm:mb-5 leading-reading max-w-xl">Board-certified plastic and reconstructive surgeon with over 17 years of clinical experience.</p>
          <p className="text-base sm:text-lg text-deep-navy/65 leading-reading max-w-xl">Combining surgical precision with an artistic eye, Dr. Jain delivers natural-looking results that enhance your unique beauty while respecting your individual character.</p>
        </div>
        <div className="flex justify-center lg:justify-end">
          <div className="relative">
            <img
              src="/dr-jain.avif"
              alt="Dr. Vicky Jain"
              loading="eager"
              fetchpriority="high"
              decoding="async"
              className="w-full max-w-md h-auto object-cover"
              data-testid="dr-jain-hero-photo"
            />
            <div className="absolute inset-0 pointer-events-none hidden lg:block" style={{background: 'linear-gradient(to right, #FAFAF8 0%, rgba(250, 250, 248, 0) 10%)'}} />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Stats Bar
const StatsBar = () => {
  const stats = [
    { number: "17+", label: "Years" },
    { number: "55+", label: "Procedures" },
    PATIENTS_STAT,
    {
      number: `${GOOGLE_BUSINESS_RATING}★`,
      label: "Rated on Google",
      labelNormalCase: true,
      href: GOOGLE_MAPS_URL,
    },
  ];
  return (
    <section
 className="bg-premium-purple py-12 md:py-16" data-testid="about-stats">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {stats.map((stat, index) => {
            const labelClass =
              stat.labelNormalCase
                ? "text-xs md:text-sm text-warm-white/80 leading-snug max-w-[14rem] mx-auto normal-case tracking-wide"
                : "text-xs md:text-sm tracking-widest uppercase text-warm-white/80 leading-snug max-w-[11rem] mx-auto";
            const inner = (
              <>
                <p className="font-serif text-4xl md:text-5xl lg:text-6xl text-gold mb-2">{stat.number}</p>
                <p className={labelClass}>{stat.label}</p>
              </>
            );
            if (stat.href) {
              return (
                <a
                  key={index}
                  href={stat.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center rounded-sm outline-none hover:opacity-95 focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-deep-navy"
                  aria-label={`${stat.label}: ${stat.number} — open Google listing`}
                >
                  {inner}
                </a>
              );
            }
            return (
              <div key={index} className="text-center">
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Biography Section
const BiographySection = () => (
  <section className="bg-warm-white py-16 md:py-24" data-testid="biography-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="max-w-4xl mx-auto">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-5 md:mb-7">Biography</h2>
        <div className="space-y-5 text-deep-navy/70 leading-reading text-[15px] sm:text-base max-w-3xl">
          <p>Dr. Vicky Jain is a board-certified plastic and reconstructive surgeon in Mumbai with over 17 years of clinical experience across the full spectrum of plastic surgery. His practice covers cosmetic surgery, reconstructive surgery, hand surgery, and pediatric plastic surgery.</p>
          <p>His dual training in cosmetic and reconstructive surgery gives patients a structural depth of understanding that goes beyond cosmetic training alone.</p>
        </div>
      </div>
    </div>
  </section>
);

// Scope of Practice Section
const ScopeOfPracticeSection = () => {
  const practices = [
    { title: "Cosmetic Surgery", description: "Facial rejuvenation, breast enhancement, body contouring, and aesthetic procedures to enhance your natural beauty.", icon: Sparkles },
    { title: "Reconstructive Surgery", description: "Post-trauma repair, cancer reconstruction, birth defect correction, and restoration of form and function.", icon: Heart },
    { title: "Hand Surgery", description: "Treatment of hand injuries, congenital conditions, carpal tunnel, and restoration of hand function.", icon: Hand },
    { title: "Pediatric Surgery", description: "Specialized care for children with congenital conditions including cleft lip and palate, and pediatric hand conditions.", icon: Baby }
  ];
  return (
    <section className="bg-warm-white py-16 md:py-24" data-testid="scope-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-6 md:mb-10 text-center px-2">Scope of Practice</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {practices.map((practice, index) => (
            <div key={index} className="bg-white p-8 border border-[#E8E2D9]" data-testid={`practice-card-${index}`}>
              <div className="w-14 h-14 rounded-none bg-gold/10 flex items-center justify-center mb-5 md:mb-6">
                <practice.icon className="w-7 h-7 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-2 md:mb-3 font-semibold tracking-tight">{practice.title}</h3>
              <p className="text-deep-navy/70 leading-reading text-[15px] sm:text-base">{practice.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Dual Expertise Section
const DualExpertiseSection = () => {
  const credentials = [
    "Board Certified Plastic Surgeon",
    "Fellowship in Cosmetic Surgery, Switzerland",
    "Fellowship in Lasers, Association of Plastic Surgeons of India",
    "Advanced Training in Microsurgery",
    "Member, Association of Plastic Surgeons of India",
    "Invited speaker at national and international conferences",
  ];
  return (
    <section className="bg-warm-white py-16 md:py-24" data-testid="dual-expertise-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-5 md:mb-8">Dual Expertise: Cosmetic &amp; Reconstructive</h2>
            <div className="space-y-5 text-deep-navy/70 leading-reading text-[15px] sm:text-base max-w-xl">
              <p>What sets Dr. Jain apart is his dual expertise in both cosmetic and reconstructive surgery. While many surgeons specialize in one or the other, Dr. Jain maintains an active practice in both disciplines, and this combination makes him uniquely qualified.</p>
              <p>His reconstructive work—rebuilding bodies after trauma, cancer, or birth defects—gives him a deep understanding of anatomy and healing that informs his cosmetic practice. His cosmetic work, in turn, brings an aesthetic sensibility to his reconstructive cases, ensuring results that are not just functional but beautiful.</p>
              <p>This dual perspective means you receive care from a surgeon who sees the complete picture—form and function, art and science, enhancement and restoration.</p>
            </div>
          </div>
          <div>
            <h3 className="font-serif text-xl text-premium-purple mb-4 md:mb-6 font-semibold tracking-tight">Credentials &amp; Affiliations</h3>
            <ul className="space-y-3 md:space-y-4">
              {credentials.map((credential, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-none bg-gold flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-deep-navy" />
                  </div>
                  <span className="text-deep-navy/80 text-[15px] sm:text-base leading-reading">{credential}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

// Consultation CTA Section
const ConsultationCTASection = () => (
  <section className="bg-premium-purple py-16 md:py-24" data-testid="about-cta-section">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
      <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-3 sm:mb-4 px-2">Schedule a Consultation</h2>
      <p className="text-warm-white/75 mb-8 max-w-2xl mx-auto leading-reading text-[15px] sm:text-base px-4">
        Meet Dr. Jain in person to discuss your goals and learn how his expertise can help you achieve natural, beautiful results.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
        <Button asChild className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base">
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            Book Consultation<ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </Button>
        <Button asChild variant="outline" className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base">
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="w-5 h-5 mr-2" />WhatsApp {PHONE_DISPLAY}
          </a>
        </Button>
      </div>
      <p className="text-warm-white/55 text-sm leading-snug px-2">Clinic Hours: Monday – Saturday, 10:00 AM – 7:00 PM</p>
    </div>
  </section>
);

const aboutStructuredData = {
  "@context": "https://schema.org",
  "@type": "Physician",
  name: "Dr. Vicky Jain",
  url: "https://drvickyjain.com/about",
  image: "https://drvickyjain.com/dr-jain.avif",
  telephone: "+91-9619489446",
  medicalSpecialty: "PlasticSurgery",
  description: "Board-certified plastic and reconstructive surgeon in Mumbai with over 17 years of experience in cosmetic, reconstructive, hand, and pediatric plastic surgery.",
  hasCredential: [
    { "@type": "EducationalOccupationalCredential", credentialCategory: "degree", name: "M.Ch. (Plastic & Cosmetic Surgery)" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "Board-Certified Plastic Surgeon" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "Fellowship in Cosmetic Surgery, Switzerland" },
    { "@type": "EducationalOccupationalCredential", credentialCategory: "certification", name: "Fellowship in Lasers, Association of Plastic Surgeons of India" },
  ],
  memberOf: [
    { "@type": "MedicalOrganization", name: "Association of Plastic Surgeons of India (APSI)" },
  ],
  address: { "@type": "PostalAddress", addressLocality: "Mumbai", addressRegion: "Maharashtra", addressCountry: "IN" },
};

const AboutPage = () => (
  <>
    <SEO title="About Dr. Vicky Jain — Board-Certified Plastic Surgeon, Mumbai" description="Learn about Dr. Vicky Jain — board-certified plastic surgeon in Mumbai with 17+ years of experience in cosmetic, reconstructive, and pediatric surgery." canonical="/about" />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutStructuredData) }} />
    <HeroSection />
    <StatsBar />
    <BiographySection />
    <ScopeOfPracticeSection />
    <DualExpertiseSection />
    <ConsultationCTASection />
  </>
);

export default AboutPage;
